import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBranding } from "../hooks/useBranding";

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const COLOR_THEMES = [
    { id: 'blue', primary: '#2563eb', bg: '#eff6ff' },
    { id: 'green', primary: '#059669', bg: '#ecfdf5' },
    { id: 'indigo', primary: '#4f46e5', bg: '#eef2ff' },
    { id: 'rose', primary: '#e11d48', bg: '#fff1f2' },
    { id: 'black', primary: '#111827', bg: '#f3f4f6' },
    { id: 'custom', primary: '#2563eb', bg: 'transparent' }, 
];

const OPTION_FIELD_TYPES = new Set(['radio', 'checkbox', 'select']);
const DEFAULT_FIELD_OPTIONS = ['選択肢1', '選択肢2'];
const AI_REFERRER_SOURCES = [
    { name: 'ChatGPT', domains: ['chatgpt.com', 'chat.openai.com'] },
    { name: 'Perplexity', domains: ['perplexity.ai', 'www.perplexity.ai'] },
    { name: 'Gemini', domains: ['gemini.google.com', 'bard.google.com'] },
    { name: 'Claude', domains: ['claude.ai'] },
    { name: 'Copilot', domains: ['copilot.microsoft.com'] },
    { name: 'Poe', domains: ['poe.com'] },
    { name: 'You.com', domains: ['you.com'] },
    { name: 'Phind', domains: ['phind.com', 'www.phind.com'] },
];

const parseReferrerHost = (value) => {
    if (!value) return '';
    try {
        return new URL(value).hostname.toLowerCase().replace(/^www\./, '');
    } catch (e) {
        return String(value).replace(/^https?:\/\//i, '').split('/')[0].toLowerCase().replace(/^www\./, '');
    }
};

const detectAiReferrer = (referrer) => {
    const host = parseReferrerHost(referrer);
    if (!host) return null;
    const source = AI_REFERRER_SOURCES.find(item =>
        item.domains.some(domain => {
            const normalized = domain.replace(/^www\./, '');
            return host === normalized || host.endsWith(`.${normalized}`);
        })
    );
    return source ? { sourceName: source.name, sourceDomain: host } : null;
};

const normalizeFieldOptions = (options) => {
    if (!Array.isArray(options)) return [...DEFAULT_FIELD_OPTIONS];
    const cleaned = options.map(option => String(option || '').trim()).filter(Boolean);
    return cleaned.length > 0 ? cleaned : [...DEFAULT_FIELD_OPTIONS];
};

export default function PublicFormPage() {
    const { appName } = useBranding();
    const { token } = useParams();
    const [formConfig, setFormConfig] = useState(null);
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        const fetchForm = async () => {
            try {
                const res = await fetch(`${API_URL}/api/public/form/${token}`);
                const data = await res.json();
                if (data.success) {
                    const fields = typeof data.form.fields === 'string' ? JSON.parse(data.form.fields) : data.form.fields;
                    const style = typeof data.form.style === 'string' ? JSON.parse(data.form.style) : data.form.style;

                    setFormConfig({
                        ...data.form,
                        fields_config: fields,
                        style_config: style
                    });
                } else {
                    setError(data.error || "フォームの読み込みに失敗しました。");
                }
            } catch (err) {
                setError("通信エラーが発生しました。");
            } finally {
                setIsLoading(false);
            }
        };
        fetchForm();
    }, [token]);

    const themeColor = useMemo(() => {
        if (!formConfig) return '#2563eb';
        const style = formConfig.style_config || {};
        if (style.theme === 'custom' && style.custom_color) {
            return style.custom_color;
        }
        return COLOR_THEMES.find(t => t.id === style.theme)?.primary || '#2563eb';
    }, [formConfig]);

    const handleChange = (e, field) => {
        const { value, checked } = e.target;
        setSubmitError(null);

        if (field.type === 'checkbox') {
            setFormData(prev => {
                const current = Array.isArray(prev[field.label]) ? prev[field.label] : [];
                const next = checked
                    ? [...new Set([...current, value])]
                    : current.filter(item => item !== value);
                return { ...prev, [field.label]: next };
            });
            return;
        }

        setFormData(prev => ({ ...prev, [field.label]: value }));
    };

    const validateRequiredFields = () => {
        const requiredField = (formConfig.fields_config || []).find((field) => {
            if (!field.required) return false;
            const value = formData[field.label];
            if (field.type === 'checkbox') return !Array.isArray(value) || value.length === 0;
            return !String(value || '').trim();
        });

        if (!requiredField) return true;
        setSubmitError(`${requiredField.label}を入力してください。`);
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateRequiredFields()) return;
        setIsSubmitting(true);
        setSubmitError(null);
        
        // ★★★ 追加ロジック：URLパラメータからトラッキングデータを抽出 ★★★
        const urlParams = new URLSearchParams(window.location.search);
        const aiSource = detectAiReferrer(document.referrer);
        const trackingData = {
            fbp: urlParams.get('fbp') || null,
            fbc: urlParams.get('fbc') || null,
            utm_source: urlParams.get('utm_source') || null,
            utm_campaign: urlParams.get('utm_campaign') || null,
            utm_medium: urlParams.get('utm_medium') || null,
            utm_content: urlParams.get('utm_content') || null,
            fb_ad_id: urlParams.get('fb_ad_id') || null,
            meta_lead_id: urlParams.get('meta_lead_id') || null,
            first_referrer: document.referrer || null,
            landing_url: window.location.href,
            source_channel: aiSource ? 'ai_search' : null,
            source_name: aiSource?.sourceName || null,
            source_domain: aiSource?.sourceDomain || null,
            source_detected_at: aiSource ? new Date().toISOString() : null
        };

        try {
            const res = await fetch(`${API_URL}/api/public/form/${token}/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // ★★★ 修正：抽出したトラッキングデータを一緒に結合して送る ★★★
                body: JSON.stringify({ 
                    data: formData, 
                    visitorId: localStorage.getItem('sb_vid'),
                    ...trackingData
                })
            });
            const result = await res.json();
            if (result.success) {
                setIsComplete(true);
                // 親フレーム全体をリダイレクトさせる
                if (result.redirect_url) setTimeout(() => window.top.location.href = result.redirect_url, 2000);
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError("送信エラーが発生しました。");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return null;
    if (error) return <div className="p-10 text-center text-red-500 font-bold">{error}</div>;

    if (isComplete) {
        return (
            <div className="p-6 sm:p-10 text-center animate-in fade-in duration-500">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="fas fa-check text-3xl"></i>
                </div>
                <h2 className="text-2xl font-black mb-4">送信が完了しました</h2>
                <p className="text-gray-500 whitespace-pre-wrap">{formConfig.success_message}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-auto py-3 sm:py-4 px-2 overflow-hidden">
            <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl sm:rounded-[2rem] overflow-hidden" 
                 style={{ 
                     boxShadow: formConfig.style_config?.show_shadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.1)' : 'none',
                     border: formConfig.style_config?.show_shadow ? 'none' : '1px solid #eee'
                 }}>
                
                <div className="p-5 sm:p-8 text-center text-white relative overflow-hidden" style={{ backgroundColor: themeColor }}>
                    <h1 className="text-xl md:text-2xl font-black relative z-10">{formConfig.title}</h1>
                    <div className="absolute top-0 left-0 w-full h-full bg-black/5"></div>
                </div>

                <form onSubmit={handleSubmit} className="p-5 sm:p-8 md:p-12 space-y-6 sm:space-y-8">
                    {formConfig.fields_config.map((field) => {
                        const options = OPTION_FIELD_TYPES.has(field.type) ? normalizeFieldOptions(field.options) : [];
                        const fieldValue = formData[field.label];

                        return (
                        <div key={field.id} className="space-y-2">
                            <label className="text-xs font-black text-gray-800 uppercase tracking-wider block">
                                {field.label} {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>

                            {field.type === 'textarea' ? (
                                <textarea
                                    required={field.required}
                                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-opacity-10 outline-none transition-all h-32"
                                    style={{ '--tw-ring-color': themeColor }}
                                    placeholder={field.placeholder}
                                    value={fieldValue || ''}
                                    onChange={(e) => handleChange(e, field)}
                                ></textarea>
                            ) : field.type === 'select' ? (
                                <select
                                    required={field.required}
                                    className="w-full min-h-14 px-4 sm:px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-opacity-10 outline-none transition-all"
                                    style={{ '--tw-ring-color': themeColor }}
                                    value={fieldValue || ''}
                                    onChange={(e) => handleChange(e, field)}
                                >
                                    <option value="">{field.placeholder || '選択してください'}</option>
                                    {options.map((option, index) => (
                                        <option key={`${field.id}_option_${index}`} value={option}>{option}</option>
                                    ))}
                                </select>
                            ) : field.type === 'radio' ? (
                                <div className="space-y-2">
                                    {options.map((option, index) => (
                                        <label key={`${field.id}_option_${index}`} className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 cursor-pointer hover:bg-white transition-all">
                                            <input
                                                type="radio"
                                                name={field.id}
                                                value={option}
                                                required={field.required}
                                                checked={fieldValue === option}
                                                onChange={(e) => handleChange(e, field)}
                                                className="h-4 w-4"
                                                style={{ accentColor: themeColor }}
                                            />
                                            <span className="text-sm font-bold text-gray-700">{option}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : field.type === 'checkbox' ? (
                                <div className="space-y-2">
                                    {options.map((option, index) => (
                                        <label key={`${field.id}_option_${index}`} className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 cursor-pointer hover:bg-white transition-all">
                                            <input
                                                type="checkbox"
                                                value={option}
                                                checked={Array.isArray(fieldValue) && fieldValue.includes(option)}
                                                onChange={(e) => handleChange(e, field)}
                                                className="h-4 w-4 rounded"
                                                style={{ accentColor: themeColor }}
                                            />
                                            <span className="text-sm font-bold text-gray-700">{option}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <input
                                    type={field.type}
                                    required={field.required}
                                    className="w-full min-h-14 px-4 sm:px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-opacity-10 outline-none transition-all"
                                    style={{ '--tw-ring-color': themeColor }}
                                    placeholder={field.placeholder}
                                    value={fieldValue || ''}
                                    onChange={(e) => handleChange(e, field)}
                                />
                            )}
                        </div>
                    );
                    })}

                    <div className="pt-6">
                        {submitError && (
                            <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600">
                                {submitError}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-5 font-black text-white shadow-xl transform active:scale-95 transition-all disabled:opacity-50"
                            style={{ 
                                backgroundColor: themeColor, 
                                borderRadius: formConfig.style_config?.border_radius || '12px' 
                            }}
                        >
                            {isSubmitting ? '処理中...' : (formConfig.style_config?.button_text || 'この内容で送信する')}
                        </button>
                    </div>
                </form>
            </div>
            <div className="text-center mt-8 text-gray-300 text-[10px] font-black uppercase tracking-widest">
                Powered by {appName}
            </div>
        </div>
    );
}
